import { Directive, forwardRef, inject, Injector } from '@angular/core';
import {
  AbstractControl,
  NG_VALIDATORS,
  NgControl,
  ValidationErrors,
  Validator,
} from '@angular/forms';

import { validateEmail } from '../validators/email.validator';

/**
 * Validator directive for email addresses.
 *
 * Validation timing:
 * - Initial validation: on blur only
 * - Re-validation after error: on input (clears instantly when valid)
 *
 * Usage:
 * ```html
 * <input type="email" [(ngModel)]="email" appEmail #emailControl="ngModel">
 * @if (emailControl.errors?.['email']) {
 *   <span>{{ getErrorMessage(emailControl.errors['email']) }}</span>
 * }
 * ```
 */
@Directive({
  selector: '[appEmail]',
  standalone: true,
  providers: [
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => EmailValidatorDirective),
      multi: true,
    },
  ],
  host: {
    '(blur)': 'onBlur()',
    '(input)': 'onInput()',
  },
})
export class EmailValidatorDirective implements Validator {
  private _injector = inject(Injector);
  private _ngControl: NgControl | null = null;

  private _hasBlurred = false;
  private _lastError: ValidationErrors | null = null;

  /**
   * Lazily resolve NgControl to avoid circular dependency.
   * NgControl depends on validators, and this directive is a validator.
   */
  private get ngControl(): NgControl | null {
    if (!this._ngControl) {
      try {
        this._ngControl = this._injector.get(NgControl, null);
      } catch {
        this._ngControl = null;
      }
    }
    return this._ngControl;
  }

  /**
   * Validator function called by Angular forms.
   */
  validate(control: AbstractControl): ValidationErrors | null {
    // Don't validate until user has blurred at least once
    if (!this._hasBlurred) {
      return null;
    }

    this._lastError = validateEmail(control.value);
    return this._lastError;
  }

  /**
   * Handle blur event - trigger validation.
   */
  onBlur(): void {
    this._hasBlurred = true;

    // Trigger validation update
    if (this.ngControl?.control) {
      this.ngControl.control.updateValueAndValidity();
    }
  }

  /**
   * Handle input event - re-validate if already has error (hybrid approach).
   */
  onInput(): void {
    if (this._hasBlurred && this._lastError && this.ngControl?.control) {
      this.ngControl.control.updateValueAndValidity();
    }
  }
}
